import enMessages from '../locales/en.json';
import arMessages from '../locales/ar.json';

type SupportedLanguage = 'en' | 'ar';

// Translation dictionaries
const translations: Record<SupportedLanguage, Record<string, string>> = {
  en: enMessages as Record<string, string>,
  ar: arMessages as Record<string, string>,
};

/**
 * Translates a backend error message to the current language
 * Tries to match common error patterns and translate them
 */
export const translateError = (errorMessage: string, language: SupportedLanguage = 'en'): string => {
  if (!errorMessage) return '';
  
  const dict = translations[language] || translations.en;
  const lowerError = errorMessage.toLowerCase().trim();
  
  // Common error patterns to translate
  const errorPatterns: Record<string, string> = {
    // Validation errors
    'required': language === 'ar' ? 'هذا الحقل مطلوب' : 'This field is required',
    'validation failed': language === 'ar' ? 'فشل التحقق من البيانات' : 'Validation failed',
    'unauthorized': language === 'ar' ? 'غير مصرح' : 'Unauthorized',
    'forbidden': language === 'ar' ? 'ممنوع' : 'Forbidden',
    'not found': language === 'ar' ? 'غير موجود' : 'Not found',
    'server error': language === 'ar' ? 'خطأ في الخادم' : 'Server error',
    'network error': language === 'ar' ? 'خطأ في الشبكة' : 'Network error',
    'timeout': language === 'ar' ? 'انتهت مهلة الاتصال' : 'Request timeout',
    
    // Common Laravel validation messages
    'the :attribute field is required': language === 'ar' ? 'هذا الحقل مطلوب' : 'This field is required',
    'the :attribute must be a valid email': language === 'ar' ? 'يجب أن يكون البريد الإلكتروني صحيحاً' : 'The email must be valid',
    'the :attribute must be unique': language === 'ar' ? 'هذه القيمة مستخدمة بالفعل' : 'This value already exists',
    'the :attribute must be at least :min characters': language === 'ar' ? 'يجب أن يكون على الأقل :min حرف' : 'Must be at least :min characters',
    'the :attribute must not be greater than :max characters': language === 'ar' ? 'يجب ألا يتجاوز :max حرف' : 'Must not exceed :max characters',
    'the selected :attribute is invalid': language === 'ar' ? 'القيمة المحددة غير صحيحة' : 'The selected value is invalid',
    
    // Work order specific
    'failed to create work order': language === 'ar' ? 'فشل إنشاء أمر العمل' : 'Failed to create work order',
    'failed to update work order': language === 'ar' ? 'فشل تحديث أمر العمل' : 'Failed to update work order',
    'work order not found': language === 'ar' ? 'أمر العمل غير موجود' : 'Work order not found',
    
    // Expense specific
    'failed to save expense': language === 'ar' ? 'فشل حفظ المصروف' : 'Failed to save expense',
    'expense not found': language === 'ar' ? 'المصروف غير موجود' : 'Expense not found',
    
    // Building specific
    'failed to save building': language === 'ar' ? 'فشل حفظ المبنى' : 'Failed to save building',
    'building not found': language === 'ar' ? 'المبنى غير موجود' : 'Building not found',
    
    // Flat specific
    'failed to save flat': language === 'ar' ? 'فشل حفظ الشقة' : 'Failed to save flat',
    'flat not found': language === 'ar' ? 'الشقة غير موجودة' : 'Flat not found',
    
    // Technician specific
    'failed to save technician': language === 'ar' ? 'فشل حفظ الفني' : 'Failed to save technician',
    'technician not found': language === 'ar' ? 'الفني غير موجود' : 'Technician not found',
    
    // User specific
    'failed to save user': language === 'ar' ? 'فشل حفظ المستخدم' : 'Failed to save user',
    'user not found': language === 'ar' ? 'المستخدم غير موجود' : 'User not found',
    
    // Role specific
    'cannot delete system role': language === 'ar' ? 'لا يمكن حذف دور النظام' : 'Cannot delete system role',
    'failed to delete role': language === 'ar' ? 'فشل حذف الدور' : 'Failed to delete role',
    'role deleted successfully': language === 'ar' ? 'تم حذف الدور بنجاح' : 'Role deleted successfully',
    
    // Generic
    'an error occurred': language === 'ar' ? 'حدث خطأ' : 'An error occurred',
    'something went wrong': language === 'ar' ? 'حدث خطأ ما' : 'Something went wrong',
  };
  
  // Try to find exact match first
  for (const [pattern, translation] of Object.entries(errorPatterns)) {
    if (lowerError.includes(pattern)) {
      return translation;
    }
  }
  
  // Try to find translation key in dictionary
  if (dict[errorMessage]) {
    return dict[errorMessage];
  }
  
  // Try common error keys
  const commonKeys = [
    `errors.${errorMessage}`,
    `messages.error.${errorMessage}`,
    `errors.generic`,
  ];
  
  for (const key of commonKeys) {
    if (dict[key]) {
      return dict[key];
    }
  }
  
  // If no translation found, return original message
  return errorMessage;
};

/**
 * Converts Western Arabic numerals (0-9) to Eastern Arabic numerals (٠-٩)
 */
const toEasternArabic = (num: string): string => {
  const easternDigits = ['٠', '١', '٢', '٣', '٤', '٥', '٦', '٧', '٨', '٩'];
  return num.replace(/\d/g, (digit) => easternDigits[parseInt(digit)]);
};

/**
 * Formats a number based on the language
 * Arabic: Uses Eastern Arabic numerals (٠-٩)
 * English: Uses Western Arabic numerals (0-9)
 */
export const formatNumber = (value: number | string | null | undefined, language: SupportedLanguage = 'en', options?: Intl.NumberFormatOptions): string => {
  if (value === null || value === undefined || value === '') {
    return language === 'ar' ? '٠' : '0';
  }
  
  const numValue = typeof value === 'string' ? parseFloat(value) : value;
  
  if (isNaN(numValue)) {
    return language === 'ar' ? '٠' : '0';
  }
  
  // Default options for currency/numbers
  const defaultOptions: Intl.NumberFormatOptions = {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
    ...options,
  };
  
  // Format with locale
  const locale = language === 'ar' ? 'ar-SA' : 'en-US';
  let formatted = new Intl.NumberFormat(locale, defaultOptions).format(numValue);
  
  // For Arabic, convert to Eastern Arabic numerals
  if (language === 'ar') {
    formatted = toEasternArabic(formatted);
  }
  
  return formatted;
};

/**
 * Formats currency based on the language
 */
export const formatCurrency = (value: number | string | null | undefined, language: SupportedLanguage = 'en', currency: string = 'KD'): string => {
  const formatted = formatNumber(value, language, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  
  // Convert currency symbol based on language
  let currencySymbol = currency;
  if (language === 'ar' && currency === 'KD') {
    currencySymbol = 'د.ك';
  }
  
  return `${formatted} ${currencySymbol}`;
};

/**
 * Formats a string that may contain numbers (like flat numbers, IDs, etc.)
 * Converts all digits in the string to Eastern Arabic numerals for Arabic
 */
export const formatStringWithNumbers = (value: string | null | undefined, language: SupportedLanguage = 'en'): string => {
  if (!value) {
    return language === 'ar' ? 'غير محدد' : 'N/A';
  }
  
  if (language === 'ar') {
    return toEasternArabic(value);
  }
  
  return value;
};

/**
 * Formats a phone number based on the language
 */
export const formatPhoneNumber = (value: string | null | undefined, language: SupportedLanguage = 'en'): string => {
  if (!value) {
    return language === 'ar' ? 'غير محدد' : 'N/A';
  }
  
  if (language === 'ar') {
    return toEasternArabic(value);
  }
  
  return value;
};

/**
 * Formats a date based on the language
 * Arabic: Uses Eastern Arabic numerals and Arabic locale
 * English: Uses Western Arabic numerals and English locale
 */
export const formatDate = (date: Date | string | null | undefined, language: SupportedLanguage = 'en', options?: Intl.DateTimeFormatOptions): string => {
  if (!date) {
    return language === 'ar' ? 'غير محدد' : 'N/A';
  }
  
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  
  if (isNaN(dateObj.getTime())) {
    return language === 'ar' ? 'تاريخ غير صحيح' : 'Invalid Date';
  }
  
  // Default options
  const defaultOptions: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    ...options,
  };
  
  // Always use English locale for dates
  const locale = 'en-US';
  const formatted = new Intl.DateTimeFormat(locale, defaultOptions).format(dateObj);

  return formatted;
};

/**
 * Formats a date and time based on the language
 */
export const formatDateTime = (date: Date | string | null | undefined, language: SupportedLanguage = 'en', options?: Intl.DateTimeFormatOptions): string => {
  const defaultOptions: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    ...options,
  };
  
  return formatDate(date, language, defaultOptions);
};

/**
 * Extracts and translates error messages from backend response
 */
export const extractAndTranslateError = (error: any, language: SupportedLanguage = 'en', t?: (key: string) => string): string => {
  if (!error) return '';
  
  // Try to get error from response
  const errorData = error?.response?.data;
  
  if (!errorData) {
    return translateError(error?.message || 'An error occurred', language);
  }
  
  // Handle Laravel validation errors
  if (errorData.errors && typeof errorData.errors === 'object') {
    const errors = errorData.errors;
    const errorMessages: string[] = [];
    
    Object.keys(errors).forEach((field) => {
      const fieldErrors = errors[field];
      const messages = Array.isArray(fieldErrors) ? fieldErrors : [fieldErrors];
      messages.forEach((msg: string) => {
        if (msg) {
          // Try to translate field name
          const fieldKey = `fields.${field}`;
          const translatedField = t && t(fieldKey) !== fieldKey ? t(fieldKey) : field;
          const translatedMsg = translateError(msg, language);
          errorMessages.push(`${translatedField}: ${translatedMsg}`);
        }
      });
    });
    
    if (errorMessages.length > 0) {
      return errorMessages.join(', ');
    }
  }
  
  // Handle single error message
  if (errorData.error) {
    return translateError(errorData.error, language);
  }
  
  if (errorData.message) {
    return translateError(errorData.message, language);
  }
  
  // Fallback
  return translateError('An error occurred', language);
};

